import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PAYMENT_ICONS } from '../../constants/paymentIcons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlatformEarningsTab = () => {
  const [earnings, setEarnings] = useState(null);
  const [payoutSettings, setPayoutSettings] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [showTransferForm, setShowTransferForm] = useState(false);

  // Editable payout settings
  const [editMethod, setEditMethod] = useState('bank_account');
  const [editAccountName, setEditAccountName] = useState('');
  const [editAccountNumber, setEditAccountNumber] = useState('');
  const [editBankName, setEditBankName] = useState('');
  const [editAutoEnabled, setEditAutoEnabled] = useState(false);
  const [editSchedule, setEditSchedule] = useState('monthly');
  const [editMinAmount, setEditMinAmount] = useState(500);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [earningsRes, settingsRes, historyRes] = await Promise.all([
        axios.get(`${API}/owner/platform-earnings`),
        axios.get(`${API}/owner/payout-settings`),
        axios.get(`${API}/owner/transfer-history`),
      ]);
      setEarnings(earningsRes.data);
      const s = settingsRes.data;
      setPayoutSettings(s);
      setEditMethod(s.payout_method || 'bank_account');
      setEditAccountName(s.payout_account_name || '');
      setEditAccountNumber(s.payout_account_number || '');
      setEditBankName(s.payout_bank_name || '');
      setEditAutoEnabled(s.auto_transfer_enabled || false);
      setEditSchedule(s.auto_transfer_schedule || 'monthly');
      setEditMinAmount(s.auto_transfer_min_amount || 500);
      setTransfers(historyRes.data.transfers || []);
    } catch (err) {
      setError('Failed to load platform earnings data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const clearMessages = () => { setTimeout(() => { setSuccess(''); setError(''); }, 4000); };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      await axios.put(`${API}/owner/payout-settings`, {
        payout_method: editMethod,
        payout_account_name: editAccountName,
        payout_account_number: editAccountNumber,
        payout_bank_name: editBankName,
        auto_transfer_enabled: editAutoEnabled,
        auto_transfer_schedule: editSchedule,
        auto_transfer_min_amount: parseFloat(editMinAmount) || 500,
      });
      setSuccess('Payout settings saved successfully');
      fetchData();
      clearMessages();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
      clearMessages();
    } finally {
      setSaving(false);
    }
  };

  const handleTransfer = async () => {
    const amt = parseFloat(transferAmount);
    if (!amt || amt <= 0) { setError('Enter a valid amount'); clearMessages(); return; }
    try {
      setTransferring(true);
      setError('');
      await axios.post(`${API}/owner/platform-transfer`, { amount: amt, notes: transferNotes });
      setSuccess(`Transfer of PHP ${amt.toLocaleString()} created successfully`);
      setTransferAmount('');
      setTransferNotes('');
      setShowTransferForm(false);
      fetchData();
      clearMessages();
    } catch (err) {
      setError(err.response?.data?.detail || 'Transfer failed');
      clearMessages();
    } finally {
      setTransferring(false);
    }
  };

  const handleCompleteTransfer = async (transferId) => {
    const reference = window.prompt(
      'Confirm you have MANUALLY sent this transfer from your PayMongo wallet (InstaPay/PesoNet/e-wallet).\n\nEnter the transaction reference number (optional):'
    );
    if (reference === null) return; // cancelled
    try {
      await axios.put(`${API}/owner/transfers/${transferId}/complete`, null, {
        params: reference ? { reference_number: reference } : {},
      });
      setSuccess('Transfer marked as completed');
      fetchData();
      clearMessages();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to complete transfer');
      clearMessages();
    }
  };

  const handleCancelTransfer = async (transferId) => {
    try {
      await axios.put(`${API}/owner/transfers/${transferId}/cancel`);
      setSuccess('Transfer cancelled');
      fetchData();
      clearMessages();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to cancel transfer');
      clearMessages();
    }
  };

  const methodLabel = (m) => m === 'gcash' ? 'GCash' : m === 'maya' ? 'Maya' : 'Bank Account';
  const methodIcon = (m) => PAYMENT_ICONS[m] || PAYMENT_ICONS.bank_account;

  if (loading) return <div className="text-center text-gray-400 py-12">Loading platform earnings...</div>;

  return (
    <div data-testid="platform-earnings-section">
      <h2 className="text-2xl font-bold text-white mb-6">Platform Earnings</h2>

      {success && <div className="mb-4 p-3 bg-green-900 border border-green-500 text-green-300 rounded-lg text-sm" data-testid="earnings-success">{success}</div>}
      {error && <div className="mb-4 p-3 bg-red-900 border border-red-500 text-red-300 rounded-lg text-sm" data-testid="earnings-error">{error}</div>}

      <div className="mb-6 p-4 bg-amber-900/30 border border-amber-600/50 rounded-lg text-sm text-amber-200" data-testid="manual-transfer-notice">
        <strong>Bookkeeping only:</strong> Your platform fees stay in your PayMongo merchant wallet. "Transfer" here is a record — it does <strong>not</strong> auto-send money. Withdraw funds yourself from PayMongo (InstaPay/PesoNet/e-wallet), then click <strong>Complete</strong> and enter the reference number. Automated PayMongo payouts are planned for a future update.
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-gray-700 rounded-xl p-5" data-testid="card-total-earnings">
          <p className="text-gray-400 text-sm mb-1">Total Platform Earnings</p>
          <p className="text-2xl font-bold text-green-400">PHP {(earnings?.total_platform_earnings || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
          <p className="text-xs text-gray-500 mt-1">{earnings?.transaction_count || 0} transactions</p>
        </div>
        <div className="bg-gray-700 rounded-xl p-5" data-testid="card-available">
          <p className="text-gray-400 text-sm mb-1">Available for Transfer</p>
          <p className="text-2xl font-bold text-blue-400">PHP {(earnings?.available_for_transfer || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
        </div>
        <div className="bg-gray-700 rounded-xl p-5" data-testid="card-pending">
          <p className="text-gray-400 text-sm mb-1">Pending Transfer</p>
          <p className="text-2xl font-bold text-yellow-400">PHP {(earnings?.pending_transfer || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
        </div>
        <div className="bg-gray-700 rounded-xl p-5" data-testid="card-transferred">
          <p className="text-gray-400 text-sm mb-1">Total Transferred</p>
          <p className="text-2xl font-bold text-white">PHP {(earnings?.total_transferred || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
        </div>
      </div>

      {/* Transfer Now Button / Form */}
      <div className="mb-8">
        {!showTransferForm ? (
          <button
            onClick={() => setShowTransferForm(true)}
            disabled={!earnings?.available_for_transfer}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg disabled:opacity-40 transition"
            data-testid="transfer-now-btn"
          >
            Transfer Now
          </button>
        ) : (
          <div className="bg-gray-700 rounded-xl p-6" data-testid="transfer-form">
            <h3 className="text-lg font-semibold text-white mb-4">Create Transfer</h3>
            <div className="flex items-center gap-3 mb-4 p-3 bg-gray-600 rounded-lg">
              <img src={methodIcon(editMethod)} alt={editMethod} className="w-8 h-8 object-contain rounded" />
              <div>
                <p className="text-white font-medium">{methodLabel(editMethod)}</p>
                <p className="text-gray-400 text-sm">{editAccountName} - {editAccountNumber}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-gray-300 text-sm mb-1 block">Amount (PHP)</label>
                <input
                  type="number"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  placeholder={`Max: ${earnings?.available_for_transfer?.toLocaleString()}`}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 outline-none"
                  data-testid="transfer-amount-input"
                  min="1"
                  max={earnings?.available_for_transfer}
                />
              </div>
              <div>
                <label className="text-gray-300 text-sm mb-1 block">Notes (optional)</label>
                <input
                  type="text"
                  value={transferNotes}
                  onChange={(e) => setTransferNotes(e.target.value)}
                  placeholder="e.g., Monthly payout"
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 outline-none"
                  data-testid="transfer-notes-input"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleTransfer}
                disabled={transferring || !transferAmount}
                className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-40 transition"
                data-testid="confirm-transfer-btn"
              >
                {transferring ? 'Processing...' : 'Confirm Transfer'}
              </button>
              <button
                onClick={() => { setShowTransferForm(false); setTransferAmount(''); setTransferNotes(''); }}
                className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-500 transition"
                data-testid="cancel-transfer-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Payout Method Configuration */}
      <div className="bg-gray-700 rounded-xl p-6 mb-8" data-testid="payout-settings">
        <h3 className="text-lg font-semibold text-white mb-4">Payout Method Configuration</h3>
        <p className="text-gray-400 text-sm mb-4">Configure where platform fees are transferred to.</p>

        {/* Method Selection */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          {['gcash', 'maya', 'bank_account'].map((method) => (
            <button
              key={method}
              onClick={() => setEditMethod(method)}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 transition ${
                editMethod === method
                  ? 'border-blue-500 bg-blue-900/30'
                  : 'border-gray-600 bg-gray-800 hover:border-gray-500'
              }`}
              data-testid={`payout-method-${method}`}
            >
              <img src={methodIcon(method)} alt={method} className="w-10 h-10 object-contain rounded" />
              <span className="text-white font-medium">{methodLabel(method)}</span>
            </button>
          ))}
        </div>

        {/* Account Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-gray-300 text-sm mb-1 block">Account Name *</label>
            <input
              type="text"
              value={editAccountName}
              onChange={(e) => setEditAccountName(e.target.value)}
              placeholder="e.g., Juan Dela Cruz"
              className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 outline-none"
              data-testid="payout-account-name"
            />
          </div>
          <div>
            <label className="text-gray-300 text-sm mb-1 block">
              {editMethod === 'bank_account' ? 'Account Number *' : 'Mobile Number *'}
            </label>
            <input
              type="text"
              value={editAccountNumber}
              onChange={(e) => setEditAccountNumber(e.target.value)}
              placeholder={editMethod === 'bank_account' ? 'e.g., 1234567890' : 'e.g., 09171234567'}
              className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 outline-none"
              data-testid="payout-account-number"
            />
          </div>
        </div>

        {editMethod === 'bank_account' && (
          <div className="mb-4">
            <label className="text-gray-300 text-sm mb-1 block">Bank Name *</label>
            <select
              value={editBankName}
              onChange={(e) => setEditBankName(e.target.value)}
              className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 outline-none"
              data-testid="payout-bank-name"
            >
              <option value="">Select Bank</option>
              <option value="BDO">BDO Unibank</option>
              <option value="BPI">Bank of the Philippine Islands (BPI)</option>
              <option value="Metrobank">Metrobank</option>
              <option value="UnionBank">UnionBank of the Philippines</option>
              <option value="Landbank">Land Bank of the Philippines</option>
              <option value="PNB">Philippine National Bank (PNB)</option>
              <option value="RCBC">Rizal Commercial Banking Corporation (RCBC)</option>
              <option value="ChinaBank">China Banking Corporation</option>
              <option value="SecurityBank">Security Bank</option>
              <option value="EastWest">EastWest Bank</option>
            </select>
          </div>
        )}

        {/* Auto-Transfer Settings */}
        <div className="border-t border-gray-600 pt-4 mt-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-white font-medium">Auto-Transfer</h4>
              <p className="text-gray-400 text-xs">Automatically create transfer requests on schedule</p>
            </div>
            <button
              onClick={() => setEditAutoEnabled(!editAutoEnabled)}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                editAutoEnabled ? 'bg-green-500' : 'bg-gray-500'
              }`}
              data-testid="auto-transfer-toggle"
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                editAutoEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {editAutoEnabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-300 text-sm mb-1 block">Schedule</label>
                <select
                  value={editSchedule}
                  onChange={(e) => setEditSchedule(e.target.value)}
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 outline-none"
                  data-testid="auto-transfer-schedule"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly (Mondays)</option>
                  <option value="monthly">Monthly (1st of month)</option>
                </select>
              </div>
              <div>
                <label className="text-gray-300 text-sm mb-1 block">Minimum Amount (PHP)</label>
                <input
                  type="number"
                  value={editMinAmount}
                  onChange={(e) => setEditMinAmount(e.target.value)}
                  min="100"
                  className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 outline-none"
                  data-testid="auto-transfer-min-amount"
                />
                <p className="text-gray-500 text-xs mt-1">Transfer only when balance exceeds this amount</p>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={handleSaveSettings}
          disabled={saving || !editAccountName || !editAccountNumber}
          className="mt-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg disabled:opacity-40 transition"
          data-testid="save-payout-settings-btn"
        >
          {saving ? 'Saving...' : 'Save Payout Settings'}
        </button>
      </div>

      {/* Transfer History */}
      <div className="bg-gray-700 rounded-xl p-6" data-testid="transfer-history">
        <h3 className="text-lg font-semibold text-white mb-4">Transfer History</h3>
        {transfers.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No transfers yet. Create your first transfer above.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-600">
                  <th className="py-3 px-3 text-gray-400 text-sm font-medium">Date</th>
                  <th className="py-3 px-3 text-gray-400 text-sm font-medium">Amount</th>
                  <th className="py-3 px-3 text-gray-400 text-sm font-medium">Method</th>
                  <th className="py-3 px-3 text-gray-400 text-sm font-medium">Type</th>
                  <th className="py-3 px-3 text-gray-400 text-sm font-medium">Status</th>
                  <th className="py-3 px-3 text-gray-400 text-sm font-medium">Reference</th>
                  <th className="py-3 px-3 text-gray-400 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((t) => (
                  <tr key={t.id} className="border-b border-gray-600/50 hover:bg-gray-600/30" data-testid={`transfer-row-${t.id}`}>
                    <td className="py-3 px-3 text-gray-300 text-sm">
                      {new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="py-3 px-3 text-white font-medium">
                      PHP {t.amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <img src={methodIcon(t.method)} alt={t.method} className="w-5 h-5 object-contain rounded" />
                        <span className="text-gray-300 text-sm">{methodLabel(t.method)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        t.type === 'auto' ? 'bg-purple-900 text-purple-300' : 'bg-blue-900 text-blue-300'
                      }`}>
                        {t.type === 'auto' ? 'Auto' : 'Manual'}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        t.status === 'completed' ? 'bg-green-900 text-green-300' :
                        t.status === 'pending' ? 'bg-yellow-900 text-yellow-300' :
                        t.status === 'processing' ? 'bg-blue-900 text-blue-300' :
                        'bg-red-900 text-red-300'
                      }`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-gray-400 text-sm">{t.reference_number || '-'}</td>
                    <td className="py-3 px-3">
                      {(t.status === 'pending' || t.status === 'processing') && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleCompleteTransfer(t.id)}
                            className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition"
                            data-testid={`complete-transfer-${t.id}`}
                          >
                            Complete
                          </button>
                          <button
                            onClick={() => handleCancelTransfer(t.id)}
                            className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition"
                            data-testid={`cancel-transfer-${t.id}`}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                      {t.status === 'completed' && (
                        <span className="text-green-400 text-xs">Done</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformEarningsTab;
